export const en = {
  // Header
  header: {
    export: 'Export Excel',
    exportSelected: 'Export Excel ({{count}})',
    projects: 'Projects',
    users: 'Users',
    addArtist: 'Add Artist',
    addUser: 'Add User',
    addProject: '+ Project',
    addProfile: '+ Profile',
    logout: 'Logout',
    goToMagicBox: 'LightBox',
    magicboxUrlNotSet: 'LightBox URL is not configured. Please set up the integration in system settings.',
    magicboxError: 'Error transitioning to LightBox',
  },

  // Login
  login: {
    title: 'VFX Spellbook',
    subtitle: 'Professional Creative Resource Management Platform',
    username: 'Username',
    password: 'Password',
    loginButton: 'Login',
    usernamePlaceholder: 'Username',
    passwordPlaceholder: 'Password',
    invalidCredentials: 'Invalid credentials',
    email: 'Email',
    emailPlaceholder: 'Email address',
    emailValidationError: 'Enter valid email',
    forgotPassword: 'Forgot your password?',
    resetPassword: 'Reset Password',
    resetEmail: 'Email Address',
    resetEmailPlaceholder: 'Enter your email address',
    resetCancel: 'Cancel',
    resetSend: 'Send Reset Email',
    resetSending: 'Sending...',
    resetSuccess: 'Password reset instructions have been sent to your email',
    resetError: 'Failed to send reset email',
    resetConnectionError: 'Connection error. Please try again.',
    recaptchaRequired: 'Please complete the reCAPTCHA verification',
    accountLocked: 'Account locked',
    tooManyAttempts: 'Too many failed attempts. Try again in',
    attemptsRemaining: 'attempt(s) remaining',
    lockedFor: 'Account locked for',
    seconds: 'seconds',
    registerLink: 'Register',
  },

  // Reset Password Page
  resetPassword: {
    title: 'Reset Password',
    subtitle: 'Enter your new password',
    newPassword: 'New Password',
    newPasswordPlaceholder: 'Enter new password',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Confirm new password',
    resetButton: 'Reset Password',
    resetting: 'Resetting...',
    backToLogin: 'Back to Login',
    fillAllFields: 'Please fill in all fields',
    passwordTooShort: 'Password must be at least 8 characters',
    passwordsMismatch: 'Passwords do not match',
    invalidToken: 'Invalid or expired reset link',
    resetFailed: 'Failed to reset password',
    connectionError: 'Connection error. Please try again.',
    successTitle: 'Password Reset Successful!',
    successMessage: 'Your password has been reset successfully.',
    redirecting: 'Redirecting to login page...',
  },

  register: {
    hubTitle: 'Create an account',
    hubSubtitle: 'Choose your profile type to submit an application',
    formTitle: 'Registration application',
    inviteFormTitle: 'Complete registration',
    organization: 'Organization / Studio',
    selectSpecialization: 'Select specialization',
    submitApplication: 'Submit application',
    submitRegister: 'Register',
    successTitle: 'Application submitted',
    successMessage:
      'Your access request was sent to the Super Admin. You will receive login credentials by email after approval.',
    artistSuccessTitle: 'Profile created',
    artistSuccessMessage: 'Your artist profile card has been created. No login access was requested.',
    inviteSuccessTitle: 'Registration complete',
    inviteSuccessMessage: 'You can now log in with your email and password.',
    backToLogin: 'Back to login',
    passwordTooShort: 'Password must be at least 8 characters',
    passwordMismatch: 'Passwords do not match',
    types: {
      staffGroup: 'Staff',
      vfxArtist: 'VFX Artist',
      vfxArtistDesc: 'External VFX artist — full portfolio application',
      vfx_artist: 'VFX Artist',
      client: 'Client',
      clientDesc: 'Client portal access',
      producer: 'Producer',
      supervisor: 'Supervisor',
      coordinator: 'Coordinator',
      sales: 'Sales',
      finder: 'Finder',
      admin: 'Admin',
    },
  },

  accessRequest: {
    toastTitle: 'Access request pending',
    toastMessage: '{{name}} ({{email}})\nClaimed role: {{role}}',
    approve: 'Approve',
    reject: 'Reject',
    approvedTitle: 'Access approved',
    approvedWithEmail: 'Login created. Password was emailed to the applicant.',
    approvedNoEmail: 'Login created, but the password email could not be sent. Check SMTP.',
    rejectedTitle: 'Access rejected',
    rejectedMessage: 'The access request was rejected.',
    actionFailed: 'Action failed',
  },

  biddingRevision: {
    toastTitle: 'Bidding revision pending',
    toastMessage: '{{editor}} edited «{{sheet}}» ({{project}})\nEdits: {{edits}}',
    approve: 'Approve',
    reject: 'Reject',
    approvedTitle: 'Revision approved',
    approvedMessage: 'Pending copy promoted to the canonical bidding sheet.',
    rejectedTitle: 'Revision rejected',
    rejectedMessage: 'Pending copy was discarded. Original sheet unchanged.',
    actionFailed: 'Revision action failed',
    bannerPending: 'You are editing a pending revision. The original sheet is unchanged until Super Admin approves.',
    bannerApproved: 'Your revision was approved and is now the live sheet. Further edits by non-admins will create a new pending copy.',
    bannerRejected: 'Your pending revision was rejected. You were returned to the original sheet.',
    dismiss: 'Dismiss',
    badgePending: 'Pending review',
  },

  profileWizard: {
    selectType: 'Who are we creating?',
    profileFields: 'Profile details',
    accessStep: 'SpellBook access',
    accessHint: 'Optionally grant login or send a registration invite link.',
  },

  profile: {
    access: {
      title: 'Access',
      hasLogin: 'Login enabled',
      noLogin: 'No login account',
      approve: 'Approve',
      grantPassword: 'Set password',
      createInvite: 'Registration link',
      copyLink: 'Copy',
      revokeLogin: 'Disable login',
      pending: 'Pending',
      active: 'Active',
      rejected: 'Rejected',
      requestAccess: 'Request Super Admin approval',
      requestSent: 'Access request sent to Super Admin',
    },
  },

  // Artist Form
  artistForm: {
    createTitle: 'Add New Artist',
    editTitle: 'Edit Artist Profile',
    personalInfo: 'Personal Information',
    name: 'Name',
    specialization: 'Specialization',
    email: 'Email',
    telegram: 'Telegram',
    portfolioUrl: 'Portfolio URL',
    description: 'Description',
    avatar: 'Avatar',
    software: 'Software',
    projectsAndAvailability: 'Projects & Availability',
    assignedProjects: 'Assigned Projects',
    projects: 'Projects',
    availability: 'Availability',
    showActiveOnly: 'Active Only',
    noActiveProjects: 'No active projects',
    selectedProjects: 'Selected',
    save: 'Save',
    cancel: 'Cancel',
    deleteAvatar: 'Delete Avatar',
    page: 'Page',
    next: 'Next',
    previous: 'Previous',
    clearDates: 'Clear',
    optional: 'optional',
    avatarUploadHint: 'Upload an image or leave empty for automatic generation',
    avatarAutoHint: '💡 Without upload: avatar is created from Gravatar (by email) or name initials',
    telegramPlaceholder: '@username or username',
    descriptionPlaceholder: 'Brief description of skills, strengths and artist experience...',
    location: 'Location',
    locationPlaceholder: 'City, country (e.g.: Moscow, Russia)',
    selectSpecialization: 'Select specialization',
    validation: {
      nameRequired: 'Name is required',
      specializationRequired: 'Specialization is required',
      contactRequired: 'Email or telegram is required',
      emailInvalid: 'Invalid email format',
      urlInvalid: 'Invalid URL format',
      softwareRecommended: 'Software recommendation',
      projectsRecommended: 'Projects recommendation',
    },
  },

  // Filter Bar
  filters: {
    search: 'Search...',
    searchUsers: 'Search by artist name...',
    searchProjects: 'Search by name or description...',
    searchBidding: 'Search by bidding name or project...',
    specialization: 'Specialization',
    software: 'Software',
    project: 'Project',
    allProjects: 'All Projects',
    client: 'Client',
    clientName: 'Client name',
    status: 'Status',
    priority: 'Priority',
    projectType: 'Project Type',
    producer: 'Producer',
    manager: 'Supervisor',
    sort: 'Sort by',
    all: 'All',
    allSpecializations: 'All Specializations',
    allProfiles: 'All Profiles',
    allStatuses: 'All Statuses',
    allPriorities: 'All Priorities',
    allTypes: 'All Types',
    allProducers: 'All Producers',
    allManagers: 'All Supervisors',
    availableToday: 'Available Today',
    profileType: 'Profile type',
    allProfileTypes: 'All profile types',
    registrationStatus: 'Application status',
    selectedCount: '{{count}} selected',
    clearAll: 'Clear all',
  },

  // Sort Options
  sort: {
    nameAsc: 'Name (A-Z)',
    nameDesc: 'Name (Z-A)',
    specializationAsc: 'Specialization (A-Z)',
    specializationDesc: 'Specialization (Z-A)',
    createdAsc: 'Created Date (A-Z)',
    createdDesc: 'Created Date (Z-A)',
  },

  // Specializations
  specializations: {
    'concept_artist': 'Concept Artist',
    '3d_modeler': '3D Modeler',
    'animator': 'Animator', 
    'fx_artist': 'FX Artist',
    'vfx_artist': 'VFX Artist', // Legacy, keep for old records
    'compositor': 'Compositor',
    'rigger': 'Rigger',
    'texture_artist': 'Texture Artist',
    'lighting_artist': 'Lighting Artist',
    'matte_painter': 'Matte Painter',
    'matchmove_artist': 'Matchmove Artist',
    'layout_artist': 'Layout Artist',
    'rotopaint_artist': 'Rotopaint Artist',
    'environment_artist': 'Environment Artist',
    'generalist': 'Generalist',
    'producer': 'Producer',
    'coordinator': 'Coordinator',
    'supervisor___teamlead': 'Supervisor / Teamlead',
    'on_set_supervisor': 'On-Set Supervisor',
    'editor': 'Editor',
    'color_grade': 'Color Grade',
    'ai___r_d_специалист': 'AI / R&D Specialist',
  },

  // User Roles
  roles: {
    'Super Admin': 'Super Admin',
    'Admin': 'Admin',
    'Producer': 'Producer',
    'Project Manager': 'Project Manager',
    'Coordinator': 'Coordinator',
    // 'Client': 'Client',  // Commented out - using Demo as Client
    'Demo': 'Client (SpellBook user)',  // Demo displays as "Client (SpellBook user)"
  },

  // Project Management
  projects: {
    title: 'Project Management',
    addProject: 'Add Project',
    name: 'Project Name',
    description: 'Description',
    startDate: 'Start Date',
    endDate: 'End Date',
    status: 'Status',
    projectType: 'Project Type',
    notSelected: 'Not Selected',
    save: 'Save',
    assignedArtists: 'Assigned Artists',
    noAssignedArtists: 'No assigned artists',
    searchArtists: 'Search artists...',
    noAvailableArtists: 'No available artists',
    saveProjectFirst: 'Save project first',
    confirmRemoveArtist: 'Remove artist from project?',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    confirmDelete: 'Delete Project',
    confirmDeleteText: 'Are you sure you want to delete project "{{name}}"?',
    dateError: 'Start date cannot be later than end date.',
    deleteMessage: 'This action cannot be undone. All assignments of this project to artists will also be deleted.',
    existingProjects: 'Existing Projects',
    noProjects: 'No projects yet.',
    createProject: 'Create Project',
    editProject: 'Edit Project',
    wizardStep1: 'Project',
    wizardStep2: 'Artists',
    wizardSkip: 'Skip',
    wizardDone: 'Done',
    createAndContinue: 'Create and continue',
    statuses: {
      planning: 'Planning',
      active: 'Active',
      completed: 'Completed',
      onHold: 'On Hold',
    },
  },

  // User Management
  userManagement: {
    title: 'System Users',
    createMode: 'Creating',
    editMode: 'Editing',
    createUser: 'Create User',
    username: 'Username',
    usernamePlaceholder: 'Enter username',
    email: 'Email',
    role: 'Role',
    password: 'Password',
    generatePassword: 'Generate Password',
    copyPassword: 'Copy Password',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    changePassword: 'Change Password',
    changePasswordTitle: 'Change User Password',
    newPassword: 'New Password',
    updatePassword: 'Update Password',
    changeEmail: 'Change Email',
    changeEmailTitle: 'Change User Email',
    changingEmailFor: 'Changing email for',
    currentEmail: 'Current Email',
    newEmail: 'New Email',
    newEmailPlaceholder: 'Enter new email address',
    updateEmail: 'Update Email',
    editEmailMode: 'Edit Email',
    userList: 'User List',
    hint: 'Hint',
    hintUsername: 'Username can be anything (email format not required)',
    hintPassword: 'Use 🔄 button to generate random password',
    hintEmail: 'Email is required for login',
    hintAdminOnly: 'Only Super Admin can manage users',
    hintChangePassword: '🔑 Use key button to change any user\'s password',
    hintChangeEmail: '📧 Use email button to change any user\'s email',
    loading: 'Loading users...',
    noUsers: 'No users found',
    changingPasswordFor: 'Changing password for',
    roles: {
      superAdmin: 'Super Admin',
      admin: 'Admin',
      producer: 'Producer',
      projectManager: 'Project Manager',
      coordinator: 'Coordinator',
      sales: 'Sales',
      // client: 'Client',  // Commented out
      demo: 'Client (SpellBook user)',  // Demo = Client (SpellBook user)
    },
    roleDescriptions: {
      superAdmin: 'Full system access, can delete any users',
      admin: 'Full access except deleting Super Admin',
      producer: 'Can manage projects and artists',
      sales: 'Limited access - only sees projects where assigned as producer',
      projectManager: 'Can manage projects',
      coordinator: 'Limited access to artists and projects',
      // client: 'Access to assigned projects only',  // Commented out
      demo: 'Access to assigned projects only (SpellBook user)',  // Demo = Client (SpellBook user)
    },
  },

  // Artist Card
  artistCard: {
    email: 'Email',
    telegram: 'Telegram',
    portfolio: 'Portfolio',
    projects: 'Projects',
    activeProjects: 'Active Projects',
    software: 'Software',
    availability: 'Availability',
    availableNow: 'Available Now',
    freeFrom: 'Free from',
    freeTo: 'to',
    needsUpdate: 'Needs update',
    noAvailability: 'No availability specified',
    noProjects: 'Not assigned',
    noActiveProjects: 'No active projects',
    noContact: 'Not specified',
    noPortfolio: 'Not specified',
    edit: 'Edit',
    delete: 'Delete',
    doubleClickToEdit: 'Double-click to edit',
  },

  // Confirmations
  confirmations: {
    deleteArtist: 'Delete Artist',
    deleteArtistText: 'Are you sure you want to delete artist "{{name}}"?',
    deleteProject: 'Delete Project',
    deleteUser: 'Delete User',
    yes: 'Yes',
    no: 'No',
    cancel: 'Cancel',
  },

  // Notifications
  notifications: {
    artistCreated: 'Artist successfully created',
    artistUpdated: 'Artist profile updated',
    artistDeleted: 'Artist deleted',
    projectCreated: 'Project created',
    projectUpdated: 'Project updated',
    projectDeleted: 'Project deleted',
    userCreated: 'User created',
    userUpdated: 'User updated',
    userDeleted: 'User deleted',
    passwordUpdated: 'Password updated',
    exportStarted: 'Export started',
    error: 'An error occurred',
  },

  // View Modes
  viewModes: {
    grid: 'Grid',
    list: 'List',
  },

  // Sort Toggle
  sortToggle: {
    sortAZ: 'Sort A-Z',
    sortZA: 'Sort Z-A',
  },

  // Calendar
  calendar: {
    months: [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ],
    weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    selectedDates: 'Selected Dates',
    clear: 'Clear',
  },

  // Language
  language: {
    ru: 'Русский',
    en: 'English',
  },

  // System Menu
  system: {
    title: 'System Menu',
    users: {
      title: 'Users',
      description: 'Create and edit system users',
      create: 'Create',
      manage: 'Manage',
      createNew: 'Create New User',
      existingUsers: 'Existing Users',
      editUser: 'Edit User',
      noUsers: 'No users found'
    },
    admin: {
      projectAccess: {
        title: 'Admin access',
        seeAllProjects: 'See all projects',
        seeAllProjectsHint: 'When off, only projects where you are assigned are visible (default)',
      },
    },
    backup: {
      title: 'Backup Management',
      description: 'Full system snapshot and restore (Super Admin only)',
      stats: 'Database Statistics',
      management: 'Backup Management',
      instructions: 'Full ZIP includes database dump, settings, and app media referenced in the database. Orphaned uploads are excluded.',
      formats: 'Supported formats: ZIP (full snapshot), JSON (data only)',
      users: 'Users',
      artists: 'Artists',
      projects: 'Projects',
      assignments: 'Assignments',
      accessDeniedDescription: 'Full backup download is available only for Super Admin',
      downloadZip: 'Download Full Backup (ZIP)',
      zipLoading: 'Building ZIP…',
      zipHint: 'Full ZIP may take several minutes on large media libraries.',
      zipSuccess: 'Full backup downloaded',
      zipFailed: 'Failed to export full backup ZIP',
      savedOnServer: 'Also saved on server',
      connectionError: 'Connection error. Please try again.',
      exportJson: 'Export JSON (data only)',
      importJson: 'Import JSON',
      trashTitle: 'Media Trash Bin',
      trashHint: 'Two-phase cleanup of orphaned uploads (files not referenced in the database).',
      trashPhaseHint: 'Phase 1: Move to Trash and verify the app. Phase 2: Empty Trash permanently. Restore anytime before Empty.',
      trashOrphans: 'Orphans on disk',
      trashInBin: 'In Trash Bin',
      trashViewOrphans: 'Orphans',
      trashViewBin: 'Trash Bin',
      trashRefresh: 'Refresh',
      trashMove: 'Move orphans to Trash',
      trashRestoreSelected: 'Restore selected',
      trashRestoreAll: 'Restore all',
      trashEmpty: 'Empty Trash',
      trashLoading: 'Scanning…',
      trashNoOrphans: 'No orphaned media found',
      trashEmptyBin: 'Trash Bin is empty',
      trashColPath: 'Path',
      trashColSize: 'Size',
      trashSelected: 'Selected: {count} · {size}',
      trashMoveSuccess: 'Moved {count} files ({size}) to Trash. Verify the app, then Empty or Restore.',
      trashMoveFailed: 'Failed to move orphans to Trash',
      trashRestoreSuccess: 'Restored {count} files ({skipped} skipped)',
      trashRestoreFailed: 'Failed to restore from Trash',
      trashSelectRestore: 'Select files to restore',
      trashEmptySuccess: 'Permanently deleted {count} files ({size})',
      trashEmptyFailed: 'Failed to empty Trash',
      trashEmptyConfirmTitle: 'Empty Trash permanently?',
      trashEmptyConfirmMessage: 'This will permanently delete {count} files ({size}). This cannot be undone.',
      biddingTrashTitle: 'Bidding Trash',
      biddingTrashHint: 'Soft-deleted estimates. Restore returns them to the bidding list. Purge deletes permanently.',
      biddingTrashSuperAdminOnly: 'Only Super Admin can restore or permanently delete estimates from trash.',
      biddingTrashCount: 'In trash: {count}',
      biddingTrashEmpty: 'No deleted estimates',
      biddingTrashColName: 'Name',
      biddingTrashColProject: 'Project',
      biddingTrashColShots: 'Shots',
      biddingTrashColDeleted: 'Deleted at',
      biddingTrashColActions: 'Actions',
      biddingTrashRestore: 'Restore',
      biddingTrashRestoreSelected: 'Restore selected',
      biddingTrashPurge: 'Delete forever',
      biddingTrashLoadFailed: 'Failed to load bidding trash',
      biddingTrashRestoreSuccess: 'Estimate restored',
      biddingTrashRestoreFailed: 'Failed to restore estimate',
      biddingTrashRestoreBatch: 'Restored {ok}, failed {failed}',
      biddingTrashPurgeSuccess: 'Estimate permanently deleted',
      biddingTrashPurgeFailed: 'Failed to permanently delete estimate',
      biddingTrashPurgeConfirmTitle: 'Delete estimate forever?',
      biddingTrashPurgeConfirmMessage: 'This cannot be undone. Shots and assignments will be removed permanently.',
    },
    activityLogs: {
      title: 'Activity Logs',
      description: 'View system audit trail and activity logs',
    },
    vfx: {
      title: 'LightBox API',
      description: 'API endpoints for LightBox integration',
      accessDenied: 'Access Denied',
      accessDeniedDescription: 'This feature is only available for Super Admin',
      integration: 'LightBox Integration',
      integrationDescription: 'SpellBook ↔ LightBox integration is active. Comments, annotations and shot statuses synchronization is available.',
      syncActive: 'Sync Active',
      pushDescription: 'Push comments and annotations from SpellBook to LightBox',
      pullDescription: 'Pull shot statuses and progress from LightBox',
      documentation: 'API documentation available in LIGHTBOX_INTEGRATION.md file',
    },
    themes: {
      title: 'Theme Editor (beta)',
      description: 'Customize application color scheme',
    },
    studio: {
      title: 'Studio Configuration',
      studioName: 'Studio Name for LightBox Integration',
      studioNameDescription: 'This name will be used by LightBox for multi-studio architecture. LightBox will use this identifier during handshake and data import.',
      studioNamePlaceholder: 'Enter studio name (e.g., Main Studio, Studio A)',
      save: 'Save',
      saved: 'Saved!',
      error: 'Error saving',
      note: 'Note',
      noteText: 'LightBox will read this setting via API endpoint',
      toIdentify: 'to identify your studio and import profiles/projects.',
    },
    ui: {
      title: 'UI Settings',
      description: 'Theme and scaling',
      theme: {
        title: 'Theme',
        current: 'Current theme',
        dark: 'Dark',
        light: 'Light',
        toggle: 'Toggle',
      },
      scaling: {
        title: 'Scaling',
        current: 'Current scale',
        description: 'Change interface element sizes',
      },
    },
    about: {
      title: 'About',
      description: 'Application information',
      version: 'Version',
      buildInfo: {
        title: 'Build Information',
        version: 'Version',
        buildDate: 'Build Date',
        buildTime: 'Build Time',
        environment: 'Environment',
      },
      features: {
        title: 'Key Features',
        artists: 'Artist management',
        projects: 'Project planning',
        backup: 'Backup system',
        roles: 'Role-based access',
      },
      tech: {
        title: 'Technologies',
      },
    },
    changelog: {
      title: 'Changelog',
      description: 'Version history and updates',
      version: 'Version',
      current: 'Current',
      previousVersions: 'Previous Versions',
      fullChangelog: 'Full changelog',
      magicbox: {
        title: 'LightBox Integration',
        syncComments: 'Sync comments and annotations SpellBook → LightBox',
        pullStatuses: 'Pull shot statuses and progress LightBox → SpellBook',
        shotMapping: 'Automatic and manual shot mapping',
        syncHistory: 'Sync history',
      },
      finance: {
        title: 'Financial System',
        transactions: 'Transaction management (income, expense, liabilities)',
        progressBars: 'Income and payment progress bars',
        liabilities: 'Liability management (pay, revert, delete)',
        grouping: 'Transaction and liability grouping',
        artistNames: 'Automatic artist name inclusion',
      },
      bidding: {
        title: 'Bidding Improvements',
        conflicts: 'Conflict detection in bulk approval',
        paidIndicators: 'Visual paid task indicators',
        duplication: 'Comment and annotation duplication',
        contextMenu: 'Context menu for all statuses',
        autoFees: 'Automatic fee liability creation',
      },
      sceneDetection: {
        title: 'Scene Detection',
        frameAccuracy: 'Frame-accurate precision (improved from 2-3 frames)',
        extraction: 'More precise frame extraction',
        boundaries: 'Improved scene boundary handling',
      },
      uiux: {
        title: 'UI/UX Improvements',
        collapsible: 'Collapsible sections in finance',
        projectMemory: 'Remember selected project',
        grouping: 'Transaction grouping',
        errorMessages: 'Improved error messages',
        bugfixes: 'Many bug fixes',
      },
      v120: {
        description: 'Client role + Email notifications',
      },
      v110: {
        description: 'Bidding and VFX edit automation',
      },
      v105: {
        description: 'Bidding system improvements',
      },
    },
  },

  // Content View Toggle
  contentView: {
    finance: 'Finance',
    users: 'Users',
    artists: 'Profiles',
    projects: 'Projects',
    teams: 'Teams',
    rates: 'Rates',
    bidding: 'Bidding',
  },

  teams: {
    dragHint: 'Shift+click to select multiple, drag onto a project',
    dropToAssign: 'Drop profiles here to assign',
    noProjects: 'No projects match the filter',
    noProfiles: 'No profiles',
    assignedCount: '{{count}} assigned',
    draggingCount: 'Selected profiles: {{count}}',
  },

  // Project List View
  projectsList: {
    noProjects: 'No projects',
    createFirst: 'Create your first project',
    progress: 'Progress',
    linkedToMagicBox: 'Linked to LightBox',
  },

  // Project Status
  projectStatus: {
    planning: 'Planning',
    active: 'Active',
    inProgress: 'In Progress',
    onHold: 'On Hold',
    completed: 'Completed',
    cancelled: 'Cancelled',
    magicbox: 'LightBox',
  },

  // Priority
  priority: {
    urgent: 'Urgent',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    critical: 'Critical', // Deprecated, use urgent
  },

  // Project Types
  projectTypes: {
    featureFilm: 'Feature Film',
    tvSeries: 'TV Series',
    commercial: 'Commercial',
    musicVideo: 'Music Video',
    gameCinematic: 'Game Cinematic',
    vrAr: 'VR/AR',
    other: 'Other',
  },

  common: {
    locale: 'en-US',
    info: 'Information',
    loading: 'Loading...',
    creating: 'Creating...',
    updating: 'Updating...',
    saving: 'Saving...',
    processing: 'Processing...',
    edit: 'Edit',
    delete: 'Delete',
    deleting: 'Deleting...',
    save: 'Save',
    cancel: 'Cancel',
    reset: 'Reset',
    previous: 'Previous',
    all: 'All',
    next: 'Next',
    add: 'Add',
    create: 'Create',
    of: 'of',
    select: 'Select',
    notAssigned: 'Not Assigned',
    fromProject: 'from project',
    notSet: 'Not Set',
    remove: 'Remove',
    project: 'Project',
    currency: 'Currency',
    budget: 'Budget',
    created: 'Created',
    itemsSelected: '{{count}} item(s) selected',
    deleteWarning: 'This action cannot be undone. All associated data will be permanently deleted.',
    close: 'Close',
    actions: 'Actions',
    optional: 'optional',
    confirmDelete: 'Confirm deletion',
    send: 'Send',
    back: 'Back',
    uses: 'uses',
    connecting: 'Connecting...',
    syncing: 'Syncing...',
    date: 'Date',
    status: 'Status',
  },

  // Public Invite
  invite: {
    buttonText: 'Invite',
    createInvite: 'Create public application link',
    modalTitle: 'Public Application Link',
    linkCreated: 'Link created successfully!',
    validFor24Hours: 'This link is valid for 24 hours',
    inviteLink: 'Application Link',
    copyLink: 'Copy link',
    copy: 'Copy',
    copied: 'Copied!',
    expiresAt: 'Expires at:',
    close: 'Close',
    instructions: 'How to use:',
    instruction1: 'Copy and share this link with potential artists',
    instruction2: 'They can fill out the application form without logging in',
    instruction3: 'You will see their profile after submission',
    tryAgain: 'Try Again',
    applicationTitle: 'Artist Application',
    applicationSubtitle: 'Fill out this form to join our artist database',
    // Form fields
    profilePicture: 'Profile Picture (Optional)',
    fullName: 'Full Name',
    email: 'Email',
    specialization: 'Specialization',
    selectSpecialization: 'Select specialization',
    software: 'Software & Tools',
    selectAtLeastOne: 'Please select at least one',
    telegram: 'Telegram (Optional)',
    phone: 'Phone (Optional)',
    location: 'Location (Optional)',
    portfolioUrl: 'Portfolio URL (Optional)',
    aboutYou: 'About You (Optional)',
    aboutPlaceholder: 'Tell us about your experience, skills, and what you\'re looking for...',
    chooseFile: 'Choose File',
    noFileChosen: 'No file chosen',
    submitApplication: 'Submit Application',
    submitting: 'Submitting...',
    // Messages
    invalidToken: 'Invalid Invite Link',
    tokenMessage: 'Please request a new invite link from the recruiter.',
    success: 'Application Submitted!',
    successMessage: 'Thank you for your application. We will review your profile and contact you soon.',
    closeWindow: 'You can close this page now.',
    captchaRequired: 'Please complete the security check',
  },

  // Activity Logs
  activityLogs: {
    title: 'Activity Logs',
    subtitle: 'Audit trail of all system activities',
    totalLogs: 'Total Logs',
    last24Hours: 'Last 24 Hours',
    topAction: 'Top Action',
    topUser: 'Most Active User',
    allActions: 'All Actions',
    allEntities: 'All Entities',
    startDate: 'Start Date',
    endDate: 'End Date',
    exportTxt: 'Export to TXT',
    deleteOld: 'Delete Logs Older Than 30 Days',
    deleteAll: 'Delete All Logs',
    confirmDeleteOld: 'Are you sure you want to delete all logs older than 30 days? This action cannot be undone.',
    confirmDeleteAll: 'Are you sure you want to delete ALL logs? This action cannot be undone and will remove the entire audit trail.',
    deleteSuccess: 'Logs deleted successfully',
    deleteError: 'Failed to delete logs',
    noLogs: 'No activity logs found',
    timestamp: 'Timestamp',
    user: 'User',
    action: 'Action',
    entity: 'Entity',
    details: 'Details',
    ip: 'IP Address',
    showing: 'Showing {{start}} to {{end}} of {{total}} logs',
  },

  // Context Menu
  contextMenu: {
    edit: 'Edit',
    delete: 'Delete',
    rename: 'Rename',
    duplicate: 'Duplicate',
    enterNewName: 'Enter new name:',
    exportToExcel: 'Export to Excel',
    assignToProject: 'Assign to Project',
    inviteToProject: 'Invite to Project',
    createBiddingFromVideo: 'VFX Montage to Estimate',
    createBiddingManual: 'Create Estimate',
    createBidding: 'Create Estimate',
    createArtist: 'Create New Artist',
    createProject: 'Create New Project',
    addUsersToProject: 'Add Users to Project',
    openInMagicBox: 'Open in LightBox',
    syncFromMagicBox: 'Sync from LightBox',
    magicboxNotConfigured: 'LightBox is not configured',
    projectNotInMagicBox: 'Project "{{name}}" is not connected to LightBox',
    magicboxError: 'Failed to open in LightBox',
    createExpenseSheet: 'Create Expense Sheet',
    setStatus: 'Set Status',
  },
  
  // Project Invitation
  projectInvitation: {
    title: 'Invite Artists to Project',
    selectedArtists: 'Selected Artists',
    selectProject: 'Select Project',
    chooseProject: 'Choose a project...',
    projectDetails: 'Project Details',
    projectName: 'Project',
    projectNameHidden: 'Project name will be hidden (NDA)',
    dates: 'Dates',
    producer: 'Producer',
    supervisor: 'Supervisor',
    description: 'Description',
    hideProjectName: 'Hide project name',
    hideProjectNameHint: 'The project name will not be shown in the invitation (NDA required)',
    invitationMethod: 'Invitation Method',
    available: 'available',
    sendInvitation: 'Send Invitation',
    someArtistsNoEmail: '{{count}} artist(s) don\'t have email addresses and won\'t receive the invitation',
    someArtistsNoTelegram: '{{count}} artist(s) don\'t have Telegram and won\'t receive the invitation',
    invitationSent: 'Invitation sent successfully!',
    invitationFailed: 'Failed to send invitation',
  },

  // Project Assignment
  projectAssignment: {
    assignTo: 'Assign to Project',
    profile: 'profile',
    profiles: 'profiles',
    searchProject: 'Search project...',
    noProjects: 'No projects found',
    assign: 'Assign',
  },

  // Add Users to Project
  addUsersToProject: {
    title: 'Add Users to Project',
    searchPlaceholder: 'Search by name, email, or specialization...',
    selectAll: 'Select All',
    selected: 'selected',
    noUsers: 'No available users',
    alreadyAssigned: 'users already assigned',
    selectUsers: 'Please select at least one user',
    add: 'Add',
    error: 'Failed to add users to project',
  },

  // Bidding System
  bidding: {
    noBiddingSheets: 'No bidding sheets',
    createFirstBidding: 'Create your first bidding sheet for project estimation',
    noBidding: 'No bidding sheets',
    createFirst: 'Create your first bidding sheet for project estimation',
    revenueTable: 'Revenue',
    expenseTable: 'Expense',
    sceneDetection: 'Scene Detection',
    videoUploaded: 'Video uploaded',
    totalBudget: 'Total Budget',
    createdBy: 'Created by',
    name: 'Name',
    namePlaceholder: 'Enter bidding name',
    nameRequired: 'Name is required',
    project: 'Project',
    projectRequired: 'Please select a project',
    video: 'Video',
    videoRequired: 'Please upload a video',
    createBiddingFromVideo: 'VFX Montage to Estimate',
    createBiddingManual: 'Create Estimate',
    createBidding: 'Create Bidding',
    editBidding: 'Edit',
    uploadVideo: 'Upload Video',
    detectScenes: 'Detect Scenes',
    biddingName: 'Bidding Name',
    selectProject: 'Select Project',
    dragDropVideo: 'Drag and drop video here or click to select',
    supportedFormats: 'Supported formats',
    selectVideo: 'Select Video',
    selectFile: 'Select File',
    invalidFileType: 'Invalid file type',
    uploading: 'Uploading video...',
    detectingScenes: 'Detecting scenes...',
    scenesDetected: 'scenes found',
    recognizingNames: 'reading names',
    detectionProgress: 'Detection progress',
    detectionPhase2: 'Phase 2: filling gaps…',
    detectionShots: 'shots',
    detectionPlayhead: 'timeline',
    detectionFailed: 'Scene detection failed',
    shotFilter: 'Shot list filter',
    shotFilterPlaceholder: 'Paste codes or numbers: 123a_0010, 0010, ABC0020…',
    shotFilterHint: 'Optional. Only matching montage shots are kept (Enter/comma).',
    shotFilterCount: 'in filter',
    shotFilterNoMatches: 'No montage shots matched the pasted list',
    insertMissingShots: 'Add unfound shots from the list',
    insertMissingShotsHint: 'After detection, remaining list items are added as empty shots (sorted). Gaps between neighbors use montage cuts; if no cut exists between matched neighbors, only a placeholder is added (see gapsWithoutCuts in status).',
    sceneDetectionInfo: 'After uploading, automatic scene detection will start',
    manualModeInfo: 'Create a table with specified number of shots for manual filling',
    shotCount: 'Number of Shots',
    shotCountPlaceholder: 'Enter number of shots',
    shotCountRequired: 'Please specify number of shots',
    shotCountHint: 'Specify the estimated number of shots (1-1000)',
    createError: 'Error creating bidding sheet',
    shotTable: 'Shot Table',
    shotsTable: 'Shot Table',
    shots: 'Shots',
    addShot: 'Add Shot',
    thumbnail: 'Thumbnail',
    shotNumber: 'Shot #',
    frameIn: 'Frame In',
    frameOut: 'Frame Out',
    timecodeIn: 'Timecode In',
    timecodeOut: 'Timecode Out',
    duration: 'Duration',
    comment: 'Comment',
    commentPlaceholder: 'Enter comment',
    teamLeadShot: 'Team Lead Shot',
    teamLead: 'Team Lead',
    hours: 'Hours',
    rate: 'Rate',
    sum: 'Sum',
    total: 'Total',
    selectArtist: 'Select Artist',
    addSpecialization: 'Add Specialization',
    newSpecialization: 'Specialization name',
    noShots: 'No shots',
    uploadVideoToDetect: 'Upload video to automatically create shots',
    bidSuffix: 'bid v01',
    selectSpecializations: 'Select specializations for columns',
    specializationsHint: 'Selected specializations will be added as columns in the table',
    specializationsRequiredTitle: 'No specializations selected',
    specializationsRequiredMessage: 'Pipeline columns are marked with *. Without them the bidding table will have no specialization columns. Select at least one, or continue without columns.',
    continueWithoutSpecializations: 'Continue without columns',
    goSelectSpecializations: 'Go select',
    doubleClickToEdit: 'Double-click to edit',
    duplicateNameError: 'A bidding sheet with this name already exists',
    
    // Annotations & Shot Names
    annotate: 'Annotate',
    shotName: 'Shot Name',
    shotNamePlaceholder: 'e.g., SHOT_0010',
    autofill: 'Autofill Shot Names',
    autofillDetected: 'Pattern detected',
    autofillConfirm: 'Apply autofill to {count} shots?',
    autofillApply: 'Apply Autofill',
    autofillCancel: 'Cancel',
    autofillPattern: 'Pattern: {pattern}',
    autofillStep: 'Step: +{step}',
    autofillButton: 'Auto-fill',
    autofillForce: 'Force auto-fill',
    autofillNoPattern: 'No pattern detected. Make sure shot names follow a consistent pattern (e.g., SHOT_0010, SHOT_0020)',
    autofillNeedTwoShots: 'Need at least 2 filled shot names to detect a pattern',
    autofillCloseAnnotation: 'Please close the annotation modal first',
    autofillAllFilled: 'All shots are already filled',

    // Settings Modal
    settings: 'Estimate Settings',
    financialSettings: 'Financial Settings',
    documentsPayment: 'Documents & Payment',
    invoiceNumber: 'Invoice #',
    contractNumber: 'Contract #',
    paymentDeadline: 'Payment Deadline',
    paymentDate: 'Payment Date',
    paymentStatus: 'Payment Status',
    statusLabel: 'Estimate Status',
    projectCurrency: 'Project currency',
    projectCurrencyHint: 'Inherited from project. Change in project settings to update all estimates.',
    
    status: {
      draft: 'Draft',
      processing: 'Processing',
      completed: 'Completed',
      failed: 'Failed',
      pending: 'Pending',
      partial: 'Partial',
      paid: 'Paid',
      overdue: 'Overdue',
      sent: 'Sent for approval',
      approved: 'Approved',
      rejected: 'Rejected',
    },
    
    statuses: {
      draft: 'Draft',
      processing: 'Processing',
      ready: 'Ready',
      sent: 'Sent for approval',
      approved: 'Approved',
      rejected: 'Rejected',
      hidden: 'Hidden',
    },
    
    // Reapprove modal
    reapproveTitle: 'Update Finances?',
    reapproveMessage: 'You have changed management fees. To reflect changes in finances, the estimate needs to be re-approved. This will update existing liabilities.',
    saveOnly: 'Save Only',
    saveAndReapprove: 'Save & Update',
    
    detectionStatuses: {
      pending: 'Pending',
      processing: 'Processing',
      completed: 'Completed',
      failed: 'Failed',
    },
    
    artistRates: {
      title: 'Artist Rates & Fees',
      button: 'Rates & Fees',
      titleExpense: "Artist's Rates",
      titleRevenue: 'Role Rates',
      buttonExpense: "Artist's Rates",
      buttonRevenue: 'Role Rates',
      description: 'Set hourly rates for project artists by specialization',
      filterBySpec: 'Filter by Specialization',
      noArtists: 'No artists in project',
      customParticipants: 'Unregistered Participants',
      addParticipantPlaceholder: 'Enter name...',
      artistsTitle: 'CG artists',
      deleteParticipantConfirm: 'Are you sure you want to remove this participant?',
      deleteSpecTitle: 'Delete specialization',
      deleteSpecConfirm: 'Delete specialization',
      deleteSpecWarning: 'This will remove all assignments for this specialization.',
      projectSpecializations: 'Project specializations',
      globalRate: 'Global rate',
      customRate: 'Custom rate',
      noSpecializations: 'No specializations. Add the first one above.',
      globalRateLabel: 'Global: {{rate}}',
    },
    
    subtotal: 'Subtotal',
    producerFee: 'Producer Fee',
    fixedAmount: 'Fixed',
    globalValue: 'Global',
    customValue: 'Custom',
    globalDefault: 'Global default',
    fromRevenue: 'from revenue',
    coordinatorFee: 'Coordinator Fee',
    supervisorFee: 'Supervisor Fee',
    managementFees: 'Management Fees',
    grandTotal: 'GRAND TOTAL',
    noArtistsForSpec: 'No artists',
    playShot: 'Play shot',
    replaceThumbnail: 'Replace thumbnail',
    deleteShot: 'Delete shot',
    renameSpecialization: 'Rename specialization',
    fillColumnWithLevel: 'Fill column with level',
    confirmFillColumn: 'Assign {{level}} to all unassigned cells in column «{{spec}}»? ({{count}} cells)',
    confirmClearColumn: 'Clear level from all assigned cells in column «{{spec}}»? ({{count}} cells)',
    renameSpecializationConflict: 'Specialization «{{name}}» already exists on this sheet',
    renameSpecializationFailed: 'Failed to rename specialization',
    noRenameTargets: 'No available pipeline roles',
    deleteColumn: 'Delete column',
    confirmDeleteColumn: 'Delete column «{{spec}}» and all assignments in it? ({{count}} items)',
    deleteColumnFailed: 'Failed to delete column',
    cannotDeleteLastColumn: 'Cannot delete the last column on this sheet',
    confirmDeleteShot: 'Are you sure you want to delete shot',
    confirmDeleteShots: 'Delete Selected Shots',
    confirmDeleteMultipleShots: 'Are you sure you want to delete {{count}} shots?',
    shotWillBeDeleted: 'This shot and all its assignments will be deleted. Shot numbers will be automatically renumbered.',
    deleteSelected: 'Delete Selected',
    deleteShots: 'Delete Shots',
    confirmDelete: 'Confirm',
    showing: 'Showing',
    of: 'of',
    perPage: 'per page',
    aggregationBySpec: 'Specialization',
    aggregationByLevel: 'Level',
    totalsRow: 'Total',
    columns: {
      hide: 'Hide column',
      unhideAll: 'Unhide all columns',
    },
    levelOther: 'Unspecified',
    levelJunior: 'Junior',
    levelMid: 'Mid',
    levelSenior: 'Senior',
    levelLead: 'Lead',
    participantPayments: 'Participant Payments',
    summary: 'Summary',
    totalShots: 'Total Shots',
    totalParticipants: 'Total Participants',
    noAssignments: 'No assignments yet',
    projectFees: 'Project Fees',
    shotUnit: 'shot',
    shotsUnit: 'shots',
    producerName: 'Producer Name',
    supervisorName: 'Supervisor Name',
    finderName: 'Finder',
    finderFee: 'Finder Fee',
    selectFinder: '— Select —',
    coordinatorName: 'Coordinator',
    enterName: 'Enter name',
    exportExcel: 'Export Excel',
    exportExcelTitle: 'Export Excel',
    exportExcelIntro: 'Choose export variant:',
    exportExcelForClient: 'For client',
    exportExcelForClientHint: 'Single “Bid” sheet — no rates or internal tabs',
    exportExcelForInternal: 'For internal use',
    exportExcelForInternalHint: 'Full workbook: shots, rates, summary (revenue + expense)',
    exportExcelExporting: 'Exporting…',
    exportError: 'Failed to export Excel',
    exportVideo: 'Export bid to video',
    exportVideoShort: 'Video',
    exportVideoTitle: 'Export bid to video',
    exportVideoIntro: 'Export {{clips}} of {{total}} shots that belong to this estimate.',
    exportVideoMissingClips: 'missing clips — will try to regenerate',
    exportVideoModePerShot: 'File per shot',
    exportVideoModeSingle: 'Single file',
    exportVideoFolderHint: 'Each shot is saved as [shot]_ref_[version].mp4 (e.g. 281_0010_ref_3.mp4). Choose an output folder.',
    exportVideoZipHint: 'Folder picker is not available in this browser — each shot downloads separately to your Downloads folder (allow multiple downloads if prompted).',
    exportVideoFilename: 'Filename',
    exportVideoFilenameHint: 'Default: project name + estimate name',
    exportVideoPreparing: 'Preparing clips…',
    exportVideoConcat: 'Concatenating video…',
    exportVideoChooseFolder: 'Choose folder & export',
    exportVideoDownloadFiles: 'Download files',
    exportVideoDownload: 'Download video',
    exportVideoSuccessSingle: 'Video downloaded',
    exportVideoSuccessPerShot: 'Saved {{count}} files to the folder',
    exportVideoSuccessSequential: 'Downloaded {{count}} files — check your Downloads folder',
    exportVideoSkipped: 'Skipped',
    exportVideoNoClips: 'No shot video clips available to export',
    exportVideoFailed: 'Video export failed',
    exportVideoCancelled: 'Export cancelled',
    exportVideoAuthError: 'Not authenticated',
    ai: {
      title: 'AI Bid Allocator',
      button: 'AI Bid',
      analyze: 'Analyze',
      allocate: 'Allocate',
      undo: 'Undo',
      analyzing: 'Analyzing shots…',
      analyzed: 'Analyzed',
      allocating: 'Allocating budget…',
      allocated: 'Done',
      undone: 'Undone',
      working: 'Working…',
      budgetRequired: 'Enter a budget',
      useProjectBudget: 'Use project budget',
      targetBudget: 'Target budget',
      feeArtistsNet: 'Budget = artists',
      feeGross: 'Gross → peel fees/VAT',
      analyses: 'Analyses',
      chatHint: 'Example: “cut shot 5 by 2x”, “cut all similar to 5 by 2x”',
      chatPlaceholder: 'Correction command…',
      column: 'AI',
      description: 'Description',
      descriptionPlaceholder: 'VFX notes…',
      analyzeFirst: 'Run Analyze first — shots have not been analyzed yet.',
      contextMode: 'Mode',
      contextColumns: 'Columns',
      specResolutionTitle: 'Detected work types',
      specResolutionHint: 'AI detected disciplines that are not on your sheet. Choose what to do for each.',
      specResolutionCreate: 'Create column',
      specResolutionRedistribute: 'Redistribute',
      specResolutionIgnore: 'Ignore',
      specResolutionShots: 'Shots',
      specResolutionWeights: 'Weight mapping (%)',
      specResolutionEven: 'Even split',
      specResolutionSum: 'Sum',
      specResolutionContinue: 'Continue allocate',
      specResolutionWeightError: 'Weights must sum to 100%',
    },
    exportParticipant: 'Export Participant',
    insertRowAbove: 'Insert row above',
    insertRowBelow: 'Insert row below',
    copyRowHours: 'Copy row hours',
    copyRowSum: 'Copy row sums',
    pasteRowHours: 'Paste row hours',
    pasteRowSum: 'Paste row sums',
    totalHours: 'Total Hours',
    totalPayment: 'Total Payment',
    payment: 'Payment',
    specialization: 'Specialization',
    profitPool: 'Profit Pool',
    vat: 'VAT',
    totalNet: 'Total (Net)',
    totalGross: 'Total with VAT',
    projectTaxRate: 'Project Tax Rate',
    projectTaxRateHint: 'Tax is calculated automatically on all income',
  },

  // Transactions
  transactions: {
    title: 'Transactions',
    selectProject: 'Select Project',
    selectProjectFirst: 'Select a project to view transactions',
    allBiddingSheets: 'All bidding sheets',
    balance: 'Balance',
    totalIncome: 'Total Income',
    totalExpense: 'Total Expense',
    addTransaction: 'Add Transaction',
    editTransaction: 'Edit Transaction',
    date: 'Date',
    category: 'Category',
    description: 'Description',
    amount: 'Amount',
    type: 'Type',
    in: 'Income',
    out: 'Expense',
    noTransactions: 'No transactions',
    noProjects: 'No available projects',
    confirmDelete: 'Are you sure you want to delete this transaction?',
    descriptionPlaceholder: 'For example: Client payment',
    filter: {
      all: 'All',
      allCategories: 'All Categories',
    },
    categoryLabels: {
      CLIENT_DEPOSIT: 'Income',
      ARTIST_PAYOUT: 'Salary',
      SOFTWARE_HARDWARE: 'Expenses',
      OTHER: 'Other',
    },
    // Liabilities
    pendingLiabilities: 'Pending',
    paidLiabilities: 'Paid',
    managementFees: 'Management Fees',
    producer: 'Producer',
    coordinator: 'Coordinator',
    supervisor: 'Supervisor',
    producerFee: 'Producer fee',
    coordinatorFee: 'Coordinator fee',
    supervisorFee: 'Supervisor fee',
    artistPayments: 'Artist Payments',
    paidToArtists: 'Paid to Artists',
    feesTotal: 'Total fees',
    noPendingLiabilities: 'No pending liabilities',
    noPaidLiabilities: 'No paid liabilities',
    totalPaid: 'TOTAL PAID:',
    budgetProgress: 'Payment Progress',
    incomeProgress: 'Income Progress',
    paymentsProgress: 'Payments Progress',
    received: 'Received',
    awaitingPayment: 'Awaiting payment',
    totalLiabilities: 'Total liabilities',
    paid: 'Paid',
    pending: 'Pending payment',
    remaining: 'Remaining',
    totalBudget: 'Budget',
    prepaymentLevel: 'Prepayment',
    groupBy: 'Group by:',
    groupByShot: 'By shot',
    groupByArtist: 'By artist',
    groupByArtistSummary: 'Artist summary',
    groupSimilar: 'Group',
    tasks: 'tasks',
    paidOf: 'Paid:',
    of: 'of',
    showDetails: 'Show details',
    pendingTasks: 'Pending payment',
    paidTasks: 'Paid',
    totalPending: 'TOTAL PENDING:',
    grandTotal: 'GRAND TOTAL:',
    pay: 'Pay',
    total: 'TOTAL:',
    // Pay all
    payAll: 'Pay All',
    confirmPayAll: 'Pay all liabilities for',
    totalAmount: 'Total amount',
    itemsCount: 'Items',
    payAllPartialSuccess: 'Paid',
    errors: 'errors',
    invalidAmount: 'Invalid amount',
    amountExceedsLiability: 'Amount exceeds liability',
    payError: 'Payment error',
    // Delete liability
    deleteLiability: 'Delete liability',
    confirmDeleteLiability: 'Delete liability?',
    deleteNotAllowed: 'Only administrators and producers can delete liabilities',
    deleteError: 'Error deleting liability',
    deleteWarning: 'This action cannot be undone. The liability will be permanently deleted.',
    artist: 'Artist',
    // Delete transaction
    deleteTransaction: 'Delete transaction',
    confirmDeleteTransaction: 'Are you sure you want to delete this transaction?',
    deleteTransactionWarning: 'This action cannot be undone. The transaction will be permanently deleted.',
    // Revert payment
    revertPayment: 'Revert payment',
    revertPaymentWarning: 'The payment will be reverted and returned to pending liabilities.',
    confirmRevertPayment: 'Return liability to pending?',
    reverting: 'Reverting...',
    revert: 'Revert payment',
    revertError: 'Error reverting payment',
    // Financial metrics (missing keys)
    totalGrossIncome: 'Total Gross Income',
    taxReserve: 'Tax Reserve',
    netCashAvailable: 'Net Cash Available',
    // Prepayment warnings
    prepaymentRequired: 'Prepayment Required',
    prepaymentRequiredText: 'Prepayment is required to start production',
    prepaymentRequiredAmount: 'Required: {{amount}}',
    prepaymentPaidAmount: 'Paid: {{amount}}',
    prepaymentRemainingAmount: 'Remaining: {{amount}}',
    prepaymentContactProducer: 'Please contact the producer to complete the prepayment.',
    // Cash gap warning
    cashGapWarning: 'CASH GAP WARNING',
    cashGapWarningText: 'Liabilities ({{liabilities}}) exceed available funds ({{available}}).',
    noProjectsAvailable: 'No projects loaded. Check API connection.',
    // Liabilities
    liabilities: 'Liabilities',
    payLiability: 'Pay Liability',
    payAmount: 'Payment Amount',
    partialPaymentHint: 'Leave empty for full payment',
    // Coverage visualization
    coveragePreview: 'Liability Coverage',
    fullyCovered: 'Fully Covered',
    partiallyCovered: 'Partially Covered',
    remainingAfterCoverage: 'Remaining After Coverage',
    noCoverage: 'This tranche will not cover liabilities',
    // Export
    export: 'Export',
    exportTooltip: 'Export transactions to CSV',
    exportError: 'Export error',
  },

  // Prepayment
  prepayment: {
    title: 'Prepayment',
    notSet: 'Not set',
    percentOfBudget: '% of budget',
    fixedAmount: 'Fixed amount',
    placeholderPercent: '0-100',
    placeholderAmount: 'Amount',
    label: 'Prepayment',
    prepaymentLabel: 'Prepayment',
    threshold: 'Prepayment threshold',
    checkTransactions: 'Prepayment required',
    secured: 'Prepayment received',
  },

  // Finance
  finance: {
    payments: 'Payments',
    paid: 'Paid',
  },

  // Profit Distribution
  profitDistribution: {
    title: 'Profit Distribution',
    available: 'Available',
    revenueTotal: 'Grand Total',
    taxReserve: 'Tax',
    expenses: 'Expenses',
    producerFee: 'Producer',
    coordinatorFee: 'Coordinator',
    distributed: 'Distributed',
    availableProfit: 'Profit',
    createDistribution: 'Create Distribution',
    distributions: 'Distributions',
    paid: 'Paid',
    approved: 'Approved',
    draft: 'Draft',
    payAll: 'Pay All',
    noProfitYet: 'No profit available for distribution yet',
    profitHint: 'Profit will become available after receiving payments and fulfilling all obligations',
    amount: 'Amount',
    maxAvailable: 'Maximum',
    distributionPreset: 'Distribution Preset',
    equalManagers: 'Equal shares among managers',
    equalArtists: 'Equal shares among artists',
    equalAll: 'Equal shares among all',
    customDistribution: 'Custom distribution',
    participants: 'Participants',
    manager: 'Manager',
    artist: 'Artist',
    totalPercentage: 'Total',
    mustBe100: 'must be 100%',
    noParticipants: 'No participants for selected preset',
    create: 'Create',
    participant: 'Participant',
    payout: 'Payout',
    totalPayout: 'Total payout',
    equalShares: 'Equal',
    resetAll: 'Reset',
    deleteDistribution: 'Delete distribution',
    confirmDelete: 'Delete this profit distribution?',
    confirmDeleteDistributed: 'Delete this distribution? All related profit payout transactions will also be deleted.',
    paidProfit: 'Profit',
    profit: 'Profit',
  },

  // Bulk Approve Modal
  bulkApprove: {
    title: 'Send to Finance',
    selectedShots: 'Selected shots',
    selectDepartments: 'Select departments to approve:',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    noDepartmentsAvailable: 'No departments available for approval',
    approve: 'Approve',
    approving: 'Approving...',
    cancel: 'Cancel',
    selectAtLeastOne: 'Select at least one department',
    approveError: 'Error approving expenses',
    allAlreadyApproved: 'All assignments for selected shots are already approved',
    fetchError: 'Failed to load department list',
    // Conflict handling
    conflictsFound: 'Conflicts Found',
    conflictsDescription: 'Some items already exist in financial liabilities. Choose an action:',
    existingTasks: 'Existing tasks',
    existingFees: 'Existing fees',
    newTasks: 'New tasks to add',
    newFees: 'New fees to add',
    current: 'Current',
    new: 'New',
    skipExisting: 'Skip existing items (add only new ones)',
    updateFees: 'Update existing fee amounts',
    confirm: 'Confirm',
  },

  // LightBox Integration
  magicbox: {
    title: 'LightBox Sync',
    connect: 'Connect',
    sync: 'Sync',
    history: 'History',
    url: 'LightBox URL',
    email: 'Email',
    password: 'Password',
    testConnection: 'Test Connection',
    connectionSuccess: 'Successfully connected to LightBox',
    connectionFailed: 'Failed to connect to LightBox',
    connectDescription: 'Connect this project to a LightBox instance to sync comments and track progress.',
    selectProject: 'Please select a LightBox project',
    selectMagicboxProject: 'Select LightBox project to link:',
    saveConnection: 'Save Connection',
    connectionSaved: 'LightBox connection saved',
    saveFailed: 'Failed to save connection',
    alreadyConnected: 'Already connected to LightBox',
    projectProgress: 'Project Progress',
    shotsCompleted: 'shots completed',
    lastSync: 'Last sync',
    pushToMagicBox: 'Push to LightBox',
    pushDescription: 'Send comments and annotations from this bidding sheet to LightBox.',
    pullFromMagicBox: 'Pull from LightBox',
    pullDescription: 'Get shot statuses and progress from LightBox.',
    push: 'Push',
    pull: 'Pull',
    noBiddingSheet: 'No bidding sheet selected',
    pushFailed: 'Failed to push to LightBox',
    pullFailed: 'Failed to pull from LightBox',
    syncResults: 'Sync Results',
    synced: 'Synced',
    errors: 'Errors',
    direction: 'Direction',
    items: 'Items',
    noHistory: 'No sync history yet',
  },

  // Rates settings
  rates: {
    title: 'Rate Settings',
    tabs: {
      virtualArtists: 'Virtual Artists',
      commissions: 'Commissions',
      taxesCurrency: 'Taxes & Currency',
      specializations: 'Specializations',
    },
    info: {
      title: 'Information:',
      description: 'Global settings are applied by default when creating new projects and bidding sheets. They can be overridden at the project level.',
    },
    virtual: {
      title: 'Virtual Artist Rates',
      description: 'Base rates for Junior / Mid / Senior / Lead artists by specialization',
      junior: 'Junior',
      mid: 'Mid',
      senior: 'Senior',
      lead: 'Lead',
      perHour: '/hour',
      saveSuccess: 'Rates saved',
      saveError: 'Error saving rates',
    },
    commissions: {
      title: 'Default Commissions',
      description: 'Default values for commissions in new bidding sheets',
      producer: 'Producer',
      coordinator: 'Coordinator',
      supervisor: 'Supervisor',
      finder: 'Finder',
      percentage: 'Percentage',
      fixed: 'Fixed',
      saveSuccess: 'Commissions saved',
    },
    taxes: {
      title: 'Taxes & Currency',
      description: 'Default financial parameters',
      defaultCurrency: 'Default Currency',
      vatRate: 'VAT Rate',
      profitTaxRate: 'Profit Tax Rate',
      saveSuccess: 'Settings saved',
    },
    specializations: {
      title: 'Specializations',
      description: 'Manage available specializations',
      addNew: 'Add Specialization',
      placeholder: 'Specialization name',
      active: 'Active',
      hidden: 'Hidden',
      moveUp: 'Move Up',
      moveDown: 'Move Down',
      hide: 'Hide',
      show: 'Show',
      delete: 'Delete',
      saveSuccess: 'Specializations saved',
    },
    common: {
      save: 'Save',
      cancel: 'Cancel',
      loading: 'Loading...',
      noPermission: 'Insufficient permissions to edit',
    },
  },

  // Review player / comments (LightBox-aligned)
  player: {
    comments: 'Comments',
    noComments: 'No comments yet for this version.',
    justNow: 'just now',
    minutesAgo: '{{count}}m ago',
    hoursAgo: '{{count}}h ago',
    daysAgo: '{{count}}d ago',
    sortByTimecode: 'Timecode',
    sortByDate: 'Date',
    reply: 'Reply',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    drawingMode: 'Draw',
    drawingModeOn: 'Drawing on',
    undo: 'Undo',
    clear: 'Clear',
    attachFile: 'Attach file',
    record: 'Record',
    stopRecording: 'Stop',
    dictate: 'Dictate',
    stopDictation: 'Stop dictation',
    send: 'Send',
    commentPlaceholder: 'Write a comment… (@ to mention)',
    replyPlaceholder: 'Write a reply…',
    fileTooLarge: 'File is too large (max {{max}} MB)',
    loopOnce: 'Play once',
    loop: 'Loop',
    bounce: 'Bounce',
    mute: 'Mute',
    unmute: 'Unmute',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit fullscreen',
    framePrev: 'Previous frame',
    frameNext: 'Next frame',
    play: 'Play',
    pause: 'Pause',
  },
};
